export const EPSILON = 1e-9;

// This is the magic formula that transforms straight lines into smooth curves
export const sigmoid = (x: number): number => 1 / (1 + Math.exp(-x));

// The reverse of the formula - we need this to go backwards from values to positions
export const logit = (y: number): number => {
  const clampedY = Math.max(EPSILON, Math.min(1 - EPSILON, y));
  return Math.log(clampedY / (1 - clampedY));
};

// If our fancy curve doesn't work for some reason, we fall back to simple straight lines
export const createLinearFunctions = (
  minPosition: number,
  maxPosition: number,
  minValue: number,
  maxValue: number,
) => {
  const valueRange = Math.max(EPSILON, maxValue - minValue);
  const positionRange = Math.max(EPSILON, maxPosition - minPosition);

  return {
    value: (pos: number): number => {
      const normalizedPos =
        (Math.max(minPosition, Math.min(maxPosition, pos)) - minPosition) / positionRange;
      return minValue + normalizedPos * valueRange;
    },
    position: (val: number): number => {
      const normalizedValue = (Math.max(minValue, Math.min(maxValue, val)) - minValue) / valueRange;
      return minPosition + normalizedValue * positionRange;
    },
  };
};

// This sets up all the logic needed for the curve transformation
export const calculateSigmoidParameters = (steepness: number, bias: number) => {
  const effectiveBias = 1 - bias;
  const sigmoidInputMin = (0 - effectiveBias) * steepness;
  const sigmoidInputMax = (1 - effectiveBias) * steepness;
  const sigmoidOutputMin = sigmoid(sigmoidInputMin);
  const sigmoidOutputMax = sigmoid(sigmoidInputMax);
  const sigmoidOutputRange = sigmoidOutputMax - sigmoidOutputMin;

  return {
    effectiveBias,
    sigmoidInputMin,
    sigmoidInputMax,
    sigmoidOutputMin,
    sigmoidOutputMax,
    sigmoidOutputRange,
  };
};

// This transforms slider positions into actual values (like prices)
export const createValueFunction = (
  minPosition: number,
  maxPosition: number,
  minValue: number,
  maxValue: number,
  steepness: number,
  effectiveBias: number,
  sigmoidOutputMin: number,
  sigmoidOutputRange: number,
) => {
  return (pos: number): number => {
    const clampedPos = Math.max(minPosition, Math.min(maxPosition, pos));
    const normalizedPos = (clampedPos - minPosition) / (maxPosition - minPosition);
    const sigmoidInput = (normalizedPos - effectiveBias) * steepness;
    const sigmoidOutput = sigmoid(sigmoidInput);
    const scaledSigmoidOutput = (sigmoidOutput - sigmoidOutputMin) / sigmoidOutputRange;
    return minValue + scaledSigmoidOutput * (maxValue - minValue);
  };
};

// This converts values (like prices) back into slider positions
export const createPositionFunction = (
  minPosition: number,
  maxPosition: number,
  minValue: number,
  maxValue: number,
  steepness: number,
  effectiveBias: number,
  sigmoidOutputMin: number,
  sigmoidOutputRange: number,
) => {
  return (val: number): number => {
    const clampedValue = Math.max(minValue, Math.min(maxValue, val));
    const normalizedValue = (clampedValue - minValue) / (maxValue - minValue);
    const sigmoidOutput = sigmoidOutputMin + normalizedValue * sigmoidOutputRange;
    const sigmoidInput = logit(sigmoidOutput);
    const normalizedPos = sigmoidInput / steepness + effectiveBias;
    const rawPosition = minPosition + normalizedPos * (maxPosition - minPosition);
    return Math.max(minPosition, Math.min(maxPosition, rawPosition));
  };
};

// Makes sure our curve settings make sense and adjusts them if they don't
export const validateAndNormalizeParameters = (rawSteepness: number, rawBias: number) => {
  const steepness = Math.max(0, rawSteepness);
  const bias = Math.max(0, Math.min(1, rawBias));

  if (rawSteepness < 0) {
    console.warn(`useSkewedScale: steepness should be >= 0. Using ${steepness}.`);
  }

  if (rawBias < 0 || rawBias > 1) {
    console.warn(`useSkewedScale: bias should be in [0, 1]. Using ${bias}.`);
  }

  return { steepness, bias };
};

// Checks if our min/max range values are sensible
export const validateRanges = (
  minValue: number,
  maxValue: number,
  minPosition: number,
  maxPosition: number,
) => {
  return minValue >= maxValue || minPosition >= maxPosition;
};
